import {
    FilterCustomViewType,
    FilterType,
    FilterValuesJoinType,
    IFilterFacet,
    IFilterFacetEnum,
    IFilterFacetGroup,
    IFilterFacetVariant,
} from 'plugins/modules/filter/types';
import {ICatalogCombo} from 'modules/products/types';
import {IFilterContext} from 'plugins/modules/filter/context/new';
import {FORCE_RELOAD_GET_PARAM, RUB_SYMBOL} from 'core/constants';
import {FILTER_URL_SLUG, RANGE_TYPES} from 'plugins/modules/filter/constants';
import {getUrlPathname} from 'core/helpers';

export const getFilterValuesJoinType = (filter: IFilterFacet) => {
    switch (filter.type) {
        case FilterType.ALONE_CHECKBOX:
            return FilterValuesJoinType.SINGLE_VALUE;

        case FilterType.CHECKBOX_OR:
        case FilterType.CHECKBOX_OR_WINERY:
        case FilterType.CHECKBOX_WITH_DESC:
            return FilterValuesJoinType.OR;

        case FilterType.CHECKBOX_AND:
            return FilterValuesJoinType.AND;

        case FilterType.RANGE:
        case FilterType.RANGE_WITH_GROUP:
            return FilterValuesJoinType.RANGE;

        default:
            return FilterValuesJoinType.SINGLE_VALUE;
    }
};

const getFilterVariantsWithProducts = (list: IFilterFacetVariant[]) => {
    return list.filter(({count, isApplied}) => isApplied || count) ?? [];
};

const getFilterEnumWithProducts = (list: IFilterFacetEnum[]) => {
    return list.filter(({count, isApplied}) => isApplied || count) ?? [];
};

export const getFiltersWithProductsOrApplied = (filterList: IFilterFacetGroup[]) => {
    const filtersWithProducts: IFilterFacet[] = [];

    filterList.forEach((filterGroup) => {
        filterGroup.items.forEach((filter) => {
            const {enum: filterEnum, variants} = filter;

            if (filter.type === FilterType.RANGE) {
                filtersWithProducts.push(filter);
                return;
            }

            if (!variants && !filterEnum) {
                if (filter.isApplied) {
                    filtersWithProducts.push(filter);
                }
                return;
            }

            const variantsWithProducts = getFilterVariantsWithProducts(variants || []);

            if (!variantsWithProducts.length && !filterEnum) {
                return;
            }

            const filterEnumFiltered = filterEnum ? getFilterEnumWithProducts(filterEnum) : undefined;
            filtersWithProducts.push({
                ...filter,
                enum: filterEnumFiltered,
                variants: variantsWithProducts,
            });
        });
    });

    return filtersWithProducts;
};

export const isFilterWithCount = (filter: IFilterFacet) => {
    return FilterCustomViewType.NO_COUNT !== filter.customView;
};

export const getFilterUnit = ({unit}: IFilterFacet) => {
    return unit ? ` ${unit}` : '';
};

const getFacetTitle = (filterList: IFilterFacetGroup[], filter: IFilterFacet) => {
    if (filter.title) {
        return filter.title;
    }

    for (const group of filterList) {
        if (group.group === filter.code) {
            return group.groupName || group.items[0]?.title;
        }

        const item = group.items.find(({code}) => code === filter.code);
        if (item) {
            return item.title || group.groupName;
        }
    }

    return filter.variants?.[0]?.title;
};

// eslint-disable-next-line complexity
const getGtagTitleForFacet = (filterList: IFilterFacetGroup[], filter: IFilterFacet, variant?: IFilterFacetVariant) => {
    if (filter.type === FilterType.ALONE_CHECKBOX) {
        return filter.variants?.[0]?.title || '';
    }

    const codeTitle = getFacetTitle(filterList, filter);

    if (filter.type === FilterType.RANGE || filter.type === FilterType.RANGE_WITH_GROUP) {
        const hasMin = null !== filter.minValue && undefined !== filter.minValue;
        const hasMax = null !== filter.maxValue && undefined !== filter.maxValue;

        if (!hasMin && !hasMax) {
            return codeTitle || '';
        }

        const titlePart = codeTitle ? `${codeTitle} - ` : '';
        // eslint-disable-next-line no-nested-ternary
        const minPart = hasMin ? `${filter.minValue}` : hasMax ? '0' : '';
        const maxPart = hasMax ? `${filter.maxValue}` : '';
        return `${titlePart}${minPart}-${maxPart}`;
    }

    const titlePart = codeTitle || '';
    const variantPart = variant ? ` - ${variant.title}` : '';

    return `${titlePart}${variantPart}`;
};

const getGtagTitleForCombo = (filterList: IFilterFacetGroup[], filter: ICatalogCombo) => {
    for (const group of filterList) {
        // eslint-disable-next-line no-restricted-syntax
        for (const item of group.items) {
            const variantFound = item.variants?.some(
                (variant) => String(variant.title).toLowerCase() === filter.title.toLowerCase(),
            );

            if (variantFound) {
                const groupTitle = item.title || group.groupName;
                return `${groupTitle} - ${filter.title}`;
            }
        }
    }

    return '';
};

export const getFilterTitleForGtag = (
    filterList: IFilterFacetGroup[],
    filter: IFilterFacet | ICatalogCombo,
    variant?: IFilterFacetVariant,
) => {
    if ('type' in filter) {
        return String(getGtagTitleForFacet(filterList, filter, variant));
    }
    return String(getGtagTitleForCombo(filterList, filter));
};

// проверка на пустоту фильтра с вариантами
export const hasValidVariants = (variants: IFilterFacetVariant[] = []): boolean => {
    return 0 < variants.filter((variant: IFilterFacetVariant) => 0 < variant.count).length;
};

// проверка на пустоту фильтра с енамами
export const hasValidEnums = (enums: IFilterFacetEnum[] = []): boolean => {
    return 0 < enums.filter((fEnum) => 0 < fEnum.count).length;
};

// проверка пустоты range фильтров
export const isValidRangeFilter = (filter: IFilterFacet): boolean => {
    const {minRange, maxRange} = filter;
    return 'number' === typeof minRange && 'number' === typeof maxRange && minRange !== maxRange;
};

export const findFilterWithValueByCombo = (
    filtersArray: IFilterContext['filterList'],
    searchObject: ICatalogCombo,
):
    | (IFilterFacet & {
          value: IFilterFacetVariant['code'] | IFilterFacetEnum['code'];
      })
    | null => {
    for (const group of filtersArray) {
        for (const item of group.items) {
            // Проверяем наличие variants или enum
            const variants = item.variants || item.enum || [];

            for (const variant of variants) {
                if (
                    variant.title.toString().toLowerCase() === searchObject.title.toLowerCase() &&
                    variant.count === searchObject.count
                ) {
                    return {...item, value: variant.code};
                }
            }
        }
    }

    return null;
};

export const isPriceFilterByUnit = (unit?: IFilterFacet['unit']) => unit === RUB_SYMBOL;

// проверка, является ли фильтр одиночным чекбоксом
export const isAloneCheckbox = (filter: IFilterFacet) => filter.type === FilterType.ALONE_CHECKBOX;

// генерация уникального ключа для элемента
export const getItemKey = (groupKey: string, filter: IFilterFacet) => `${groupKey}-${filter.code}-${filter.title}`;

export const isValidFilter = (filter: IFilterFacet): boolean => {
    const {type, variants, enum: enums} = filter;

    // проверка валидности variants
    if (variants && !hasValidVariants(variants)) {
        return false;
    }

    // проверка валидности enum
    if (enums && !hasValidEnums(enums)) {
        return false;
    }

    if (RANGE_TYPES.includes(type) && !isValidRangeFilter(filter)) {
        return false;
    }

    // проверка range фильтров
    return true;
};

const getPathnameWithoutFilters = (pathname: string) => {
    const [pathnameBase] = pathname.split(FILTER_URL_SLUG);

    return pathnameBase.endsWith('/') ? pathnameBase : `${pathnameBase}/`;
};

// смена url без учёта фильтров в пути: выбор фильтра не считается переходом на другую страницу
export const isUrlChangedIgnoringFilters = (nextUrl: string): boolean => {
    const {searchParams} = new URL(nextUrl, window.location.origin);

    const currentPathname = getPathnameWithoutFilters(window.location.pathname);
    const nextPathname = getPathnameWithoutFilters(getUrlPathname(nextUrl));

    return currentPathname !== nextPathname || Boolean(searchParams.get(FORCE_RELOAD_GET_PARAM));
};
